<template>
  <div class="storybook-container">
    <!-- Header Section -->
    <header class="storybook-header">
      <h1 class="library-title">Vue.js UI Component Library</h1>
      <p class="library-subtitle">
        A comprehensive collection of reusable Vue components
      </p>
    </header>

    <!-- Navigation -->
    <nav class="component-nav">
      <h3>Components</h3>
      <ul class="nav-list">
        <li><a href="#layout">Layout Components</a></li>
        <li><a href="#typography">Typography</a></li>
        <li><a href="#data-display">Data Display</a></li>
        <li><a href="#form-controls">Form Controls</a></li>
        <li><a href="#feedback">Feedback</a></li>
        <li><a href="#navigation">Navigation</a></li>
        <li><a href="#utils">Utilities</a></li>
      </ul>
    </nav>

    <!-- Main Content -->
    <main class="storybook-main">
      <!-- Layout Components Section -->
      <section id="layout" class="component-section">
        <h2 class="section-title">Layout Components</h2>

        <!-- Flex Component Story -->
        <div class="component-story">
          <h3 class="story-title">Flex Component</h3>
          <p class="story-description">
            A flexible container component for creating responsive layouts
          </p>

          <div class="story-showcase">
            <flex
              id="flex-1"
              className="bordered"
              justifyContent="space-around"
              flex-wrap="wrap"
              paddingSx="0"
            >
              <div class="item" style="width: 100%">
                This is a flex-box component 100% width
              </div>
              <div class="item" style="width: 50%">50% width</div>
              <div class="item" style="width: 50%">50% width</div>
            </flex>
          </div>
        </div>

        <!-- Grid Component Story -->
        <div class="component-story">
          <h3 class="story-title">Grid Component</h3>
          <p class="story-description">
            Responsive grid system with breakpoint support
          </p>

          <div class="story-showcase">
            <GridRow gutter="2" justify="center">
              <GridCol :cols="12" :md="6" :lg="3">
                <div class="box">Column 1</div>
              </GridCol>
              <GridCol :cols="12" :md="6" :lg="3">
                <div class="box">Column 2</div>
              </GridCol>
              <GridCol :cols="12" :md="12" :lg="3"></GridCol>
              <GridCol :cols="12" :md="12" :lg="3">
                <div class="box">Column 3</div>
              </GridCol>
            </GridRow>
          </div>
        </div>

        <!-- Divider Component Story -->
        <div class="component-story">
          <h3 class="story-title">Divider Component</h3>
          <p class="story-description">
            Visual separators for content sections
          </p>

          <div class="story-showcase">
            <Divider
              orientation="horizontal"
              :thickness="2"
              :_sx="{ width: 400 }"
            />
            <br />
            <Divider
              orientation="vertical"
              :thickness="2"
              :_sx="{ height: 60 }"
            />
          </div>
        </div>
      </section>

      <!-- Typography Section -->
      <section id="typography" class="component-section">
        <h2 class="section-title">Typography</h2>

        <div class="component-story">
          <h3 class="story-title">Typography Component</h3>
          <p class="story-description">
            Consistent text styling across different variants
          </p>

          <div class="story-showcase">
            <Typography
              id="drip-typography-h1"
              class="drip-typography"
              variant="h1"
              currentTag="h1"
              color="noble-blue-500"
              fontWeight="700"
              textTransform="capitalize"
              textAlign="center"
            >
              This is a H1 Typography component
            </Typography>

            <Typography
              id="drip-typography-h2"
              class="drip-typography"
              variant="h2"
              currentTag="h2"
              color="noble-blue-500"
              fontWeight="700"
              textTransform="capitalize"
              textAlign="center"
            >
              This is a H2 Typography component
            </Typography>

            <Typography
              v-for="varnt in [
                'h3',
                'h4',
                'h5',
                'caption',
                'body',
                'body-small',
                'label',
                'validation'
              ]"
              :key="varnt"
              :id="`drip-typography-${varnt}`"
              class="drip-typography"
              :variant="varnt"
              currentTag="div"
              color="noble-blue-500"
              fontWeight="400"
              textAlign="center"
            >
              This is a <u>{{ varnt }}</u> typography component
            </Typography>
          </div>
        </div>
      </section>

      <!-- Data Display Section -->
      <section id="data-display" class="component-section">
        <h2 class="section-title">Data Display</h2>

        <!-- Table Component Story -->
        <div class="component-story">
          <h3 class="story-title">Table Component</h3>
          <p class="story-description">
            Data table with sorting and pagination
          </p>

          <div class="story-showcase">
            <DripTable
              :items="filteredUsers"
              :fields="[
                { key: 'name', label: 'Full Name', sortable: true },
                { key: 'email', label: 'Email Address', sortable: false },
                { key: 'role', label: 'Role Assigned', sortable: true }
              ]"
            />
            <DripTablePagination
              :total-rows="users.length"
              v-model="currentPage"
              :per-page="5"
            />
          </div>
        </div>

        <!-- Badge Component Story -->
        <div class="component-story">
          <h3 class="story-title">Badge Component</h3>
          <p class="story-description">
            Small status descriptors for UI elements
          </p>

          <div class="story-showcase">
            <Badge badgeContent="3+">
              <h1>H</h1>
            </Badge>
          </div>
        </div>

        <!-- Chip Component Story -->
        <div class="component-story">
          <h3 class="story-title">Chip Component</h3>
          <p class="story-description">
            Compact elements representing inputs, attributes, or actions
          </p>

          <div class="story-showcase">
            <div class="chip-group">
              <Chip variant="success">Completed chip</Chip>
              <Chip variant="error">
                <b-icon-arrow-left-right class="mr-2" />
                Error chip
              </Chip>
              <Chip variant="warning">Warning chip</Chip>
              <Chip variant="info">Info Chip</Chip>
            </div>
          </div>
        </div>

        <!-- Skeleton Component Story -->
        <div class="component-story">
          <h3 class="story-title">Skeleton Component</h3>
          <p class="story-description">
            Loading placeholder with shimmer animation
          </p>

          <div class="story-showcase">
            <Skeleton
              animation="wave"
              height="50px"
              width="80%"
              variant="rectangular"
              class-name="my-skeleton"
              :_sx="{ backgroundColor: '#ccc' }"
            />
          </div>
        </div>

        <!-- Progress Component Story -->
        <div class="component-story">
          <h3 class="story-title">Circular Progress</h3>
          <p class="story-description">
            Loading indicator for async operations
          </p>

          <div class="story-showcase">
            <CircularProgress
              :variant="'indeterminate'"
              :size="60"
              :thickness="4"
              className="my-progress"
              dataTestId="progress-indeterminate"
            />
          </div>
        </div>
      </section>

      <!-- Form Controls Section -->
      <section id="form-controls" class="component-section">
        <h2 class="section-title">Form Controls</h2>

        <!-- Text Field Story -->
        <div class="component-story">
          <h3 class="story-title">Text Field</h3>
          <p class="story-description">Input field with validation support</p>

          <div class="story-showcase">
            <form @submit.prevent="testInputForm" novalidate class="demo-form">
              <TextField
                id="test_input"
                label="Test Input"
                name="name"
                v-model="form.name"
                placeholder="Enter name"
                rules="required|remove_specials"
              />
              <button type="submit" class="demo-button">Submit Form</button>
            </form>
          </div>
        </div>

        <!-- Select Field Story -->
        <div class="component-story">
          <h3 class="story-title">Select Field</h3>
          <p class="story-description">Dropdown selection component</p>

          <div class="story-showcase">
            <form @submit.prevent="testSelectForm" novalidate class="demo-form">
              <Select
                label="Select currency"
                placeholder="Select..."
                v-model="form.currency"
                :options="['USD', 'INR', 'EUR']"
              />
              <button type="submit" class="demo-button">Submit Form</button>
            </form>
          </div>
        </div>

        <!-- Switch Component Story -->
        <div class="component-story">
          <h3 class="story-title">Switch Component</h3>
          <p class="story-description">Toggle switch for boolean values</p>

          <div class="story-showcase">
            <DripSwitch
              :switchState="currentState"
              :changeState="changeState"
            />
          </div>
        </div>

        <!-- Checkbox Story -->
        <div class="component-story">
          <h3 class="story-title">Checkbox Component</h3>
          <p class="story-description">
            Multi-select input with various states
          </p>

          <div class="story-showcase">
            <div class="checkbox-group">
              <CheckBox v-model="form.isChecked" id="agree">
                I agree to the terms and conditions
              </CheckBox>
              <CheckBox
                v-model="form.isChecked"
                id="agree-disabled"
                :disabled="true"
              >
                I agree to the terms and conditions [Disabled]
              </CheckBox>
              <p class="checkbox-status">
                Checkbox is: {{ form.isChecked ? 'Checked' : 'Unchecked' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Radio Button Story -->
        <div class="component-story">
          <h3 class="story-title">Radio Button Component</h3>
          <p class="story-description">Single-select input component</p>

          <div class="story-showcase">
            <form @submit.prevent="submitRadioForm" class="demo-form">
              <RadioButton
                v-model="form.radio"
                :options="meta.DOCS_ROUTING"
                name="radio"
              />
              <button type="submit" class="demo-button btn-primary">
                Submit
              </button>
            </form>
          </div>
        </div>

        <!-- Date Picker Story -->
        <div class="component-story">
          <h3 class="story-title">Date Picker</h3>
          <p class="story-description">Calendar-based date selection</p>

          <div class="story-showcase">
            <DatePicker
              id="startDate"
              name="startDate"
              label="Start Date"
              placeholder="Select a date"
              v-model="startDate"
              rules="required"
            />
          </div>
        </div>
      </section>

      <!-- Feedback Section -->
      <section id="feedback" class="component-section">
        <h2 class="section-title">Feedback</h2>

        <!-- Alert Component Story -->
        <div class="component-story">
          <h3 class="story-title">Alert Component</h3>
          <p class="story-description">Status messages and notifications</p>

          <div class="story-showcase">
            <div class="alert-group">
              <Alert
                type="info"
                title="This is an info alert"
                subtitle="This is subtitle of an alert"
              />
              <Alert
                type="success"
                title="This is a success alert"
                subtitle="This is subtitle of an alert"
              />
              <Alert
                type="danger"
                title="This is an error alert"
                subtitle="This is subtitle of an alert"
              />
              <Alert
                type="warning"
                title="This is a warning alert"
                subtitle="This is subtitle of an alert"
              />
            </div>
          </div>
        </div>

        <!-- Toast Notification Story -->
        <div class="component-story">
          <h3 class="story-title">Toast Notification</h3>
          <p class="story-description">Temporary notification messages</p>

          <div class="story-showcase">
            <ToastNotification />
            <div class="toast-controls">
              <button @click="openToast('success')" class="demo-button success">
                Success Toast
              </button>
              <button @click="openToast('error')" class="demo-button error">
                Error Toast
              </button>
              <button @click="openToast('warn')" class="demo-button warning">
                Warning Toast
              </button>
            </div>
          </div>
        </div>

        <!-- Dialog Box Story -->
        <div class="component-story">
          <h3 class="story-title">Dialog Box Component</h3>
          <p class="story-description">Modal dialogs for user interactions</p>

          <div class="story-showcase">
            <DialogBox
              v-if="form.showDialog"
              dialogWidth="600px"
              @close="closeDialog"
            >
              <template #header>Heading of dialog box</template>
              <template #body>Body of dialog box</template>
            </DialogBox>
            <button @click="openDialog" class="demo-button">Open Dialog</button>
          </div>
        </div>
      </section>

      <!-- Navigation Section -->
      <section id="navigation" class="component-section">
        <h2 class="section-title">Navigation</h2>

        <!-- Link Component Story -->
        <div class="component-story">
          <h3 class="story-title">Link Component</h3>
          <p class="story-description">
            Navigation links with various states and styles
          </p>

          <div class="story-showcase">
            <div class="link-group">
              <Link href="https://www.google.com" target="_blank">
                <b-icon-arrow-left-right /> Left icon link
              </Link>
              <Link href="https://www.google.com" target="_blank">
                Right Icon Link <b-icon-arrow-left-right />
              </Link>
              <Link href="https://www.google.com" target="_blank" disabled>
                Disabled Link
              </Link>
              <Link
                href="/terms"
                target="_blank"
                :_sx="{ color: 'red', fontWeight: 'bold' }"
              >
                Colored link
              </Link>
            </div>
          </div>
        </div>

        <!-- Breadcrumbs Story -->
        <div class="component-story">
          <h3 class="story-title">Breadcrumbs Component</h3>
          <p class="story-description">Navigation hierarchy indicator</p>

          <div class="story-showcase">
            <BreadCrumbs
              page="Main link"
              section="Section page"
              :pageClick="openLink"
            />
          </div>
        </div>

        <!-- Tabs Component Story -->
        <div class="component-story">
          <h3 class="story-title">Tabs Component</h3>
          <p class="story-description">
            Content organization with tabbed interface
          </p>

          <div class="story-showcase">
            <Tabs
              :tabOptions="['Item 1', 'Item 2', 'Item 3']"
              :tabSelected="tabSelected"
              :setTab="setTab"
            />
          </div>
        </div>

        <!-- Accordion Story -->
        <div class="component-story">
          <h3 class="story-title">Accordion Component</h3>
          <p class="story-description">Collapsible content sections</p>

          <div class="story-showcase">
            <Accordion :openAccordian="true">
              <template v-slot:title-left>Accordion title</template>
              <template v-slot:body>
                Accordion body content with expandable sections for organizing
                information in a space-efficient manner.
              </template>
            </Accordion>
          </div>
        </div>

        <!-- Sidebar Story -->
        <div class="component-story">
          <h3 class="story-title">Sidebar Component</h3>
          <p class="story-description">Collapsible side navigation panel</p>

          <div class="story-showcase">
            <Sidebar
              :isOpen="form.isSidebarOpen"
              :toggleSidebar="toggleSidebar"
              :rightSide="true"
            >
              <template #sidebar-content>
                <h5>Sidebar Title</h5>
              </template>
              <template #main-content>
                <h3>Main Area</h3>
                <p>This is where your app content goes.</p>
              </template>
            </Sidebar>
            <button @click="toggleSidebar()" class="demo-button">
              Toggle Sidebar
            </button>
          </div>
        </div>
      </section>

      <!-- Utils Section -->
      <section id="utils" class="component-section">
        <h2 class="section-title">Utilities</h2>

        <!-- Button Component Story -->
        <div class="component-story">
          <h3 class="story-title">Action Button</h3>
          <p class="story-description">
            Primary action buttons with different variants
          </p>

          <div class="story-showcase">
            <action-button
              variant="solid"
              type="submit"
              extraClass="pt-3 text-center"
            >
              CTA Button
            </action-button>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
<script>
import Flex from '@/vue-library/Flex'
import GridCol from '@/vue-library/GridCol'
import GridRow from '@/vue-library/GridRow'
import Typography from '@/vue-library/Typography'
import Tabs from '@/vue-library/Tabs'
import DripTable from '@/vue-library/DripTable'
import DripTablePagination from '@/vue-library/DripTablePagination'
import DripSwitch from '@/vue-library/DripSwitch'
import TextField from '@/vue-library/TextField'
import Select from '@/vue-library/Select'
import Divider from '@/vue-library/Divider'
import Link from '@/vue-library/Link'
import Badge from '@/vue-library/Badge'
import CheckBox from '@/vue-library/CheckBox'
import CircularProgress from '@/vue-library/CircularProgress'
import DatePicker from '@/vue-library/DatePicker'
import Skeleton from '@/vue-library/Skeleton'
import RadioButton from '@/vue-library/RadioButton'
import Accordion from '@/vue-library/Accordion'
import Alert from '@/vue-library/Alert'
import ActionButton from '@/vue-library/ActionButton'
import Chip from '@/vue-library/Chip'
import BreadCrumbs from '@/vue-library/BreadCrumbs'
import { BIconArrowLeftRight } from 'bootstrap-vue'
import ToastNotification from '@/vue-library/ToastNotification'
import DialogBox from '@/vue-library/DialogBox'
import Sidebar from '@/vue-library/Sidebar'

export default {
  components: {
    Sidebar,
    Flex,
    GridCol,
    GridRow,
    Typography,
    DatePicker,
    CircularProgress,
    CheckBox,
    Badge,
    Divider,
    Link,
    Tabs,
    DripTable,
    DripTablePagination,
    DripSwitch,
    TextField,
    Select,
    Skeleton,
    RadioButton,
    Accordion,
    Alert,
    ActionButton,
    BIconArrowLeftRight,
    Chip,
    BreadCrumbs,
    ToastNotification,
    DialogBox
  },
  data() {
    return {
      form: {
        name: '',
        currency: '',
        radio: '',
        selectedDate: '',
        isChecked: false,
        showDialog: false,
        isSidebarOpen: false
      },
      meta: {
        DOCS_ROUTING: [
          { text: 'Email', value: 'email' },
          { text: 'Courier', value: 'courier' },
          { text: 'Fax', value: 'fax' }
        ]
      },
      currentState: 'on',
      tabSelected: 'Item 1',
      currentPage: 1,
      users: [
        { name: 'Alice', email: 'alice@example.com', role: 'Admin' },
        { name: 'Bob', email: 'bob@example.com', role: 'User' },
        { name: 'Charlie', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' },
        { name: 'DarlieX', email: 'charlie@example.com', role: 'Editor' }
      ],
      progress: 60,
      picked: null,
      startDate: ''
    }
  },
  mounted() {
    this.$mpTrack('Library page opened')
  },
  methods: {
    toggleSidebar() {
      this.form.isSidebarOpen = !this.form.isSidebarOpen
    },
    openDialog() {
      this.form.showDialog = true
    },
    closeDialog() {
      this.form.showDialog = false
    },
    testInputForm() {
      this.$validator.validate().then((res) => {
        if (res) {
          alert('testInputForm is valid!')
        }
      })
    },
    testSelectForm() {
      this.$validator.validate().then((res) => {
        if (res) {
          alert('testSelectForm is valid!')
        }
      })
    },
    submitRadioForm() {
      this.$validator.validateAll().then((valid) => {
        if (valid) {
          alert('submitRadioForm is valid!')
        }
      })
    },
    setTab(tab) {
      this.tabSelected = tab
    },
    changeState() {
      if (this.currentState === 'on') {
        this.currentState = 'off'
      } else {
        this.currentState = 'on'
      }
    },
    openLink() {
      window.open('https://www.google.com')
    },
    openToast(type) {
      this.$notify({
        title: this.$t(`networkErrList.title`),
        text: this.$t(`networkErrList.defaultMsg`),
        type
      })
    },
    onChange(date) {
      console.log('Change:', date)
    },
    onAccept(date) {
      console.log('Accept:', date)
    },
    onError(err) {
      console.warn('Error:', err)
    }
  },
  computed: {
    filteredUsers() {
      const startNo = 5 * (this.currentPage - 1)
      return this.users.slice(startNo, startNo + 5)
    }
  }
}
</script>

<style scoped>
.storybook-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #333;
  line-height: 1.6;
}

.storybook-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem 2rem;
  text-align: center;
  margin-bottom: 2rem;
}

.library-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.library-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin: 0;
}

.component-nav {
  background: #f8f9fa;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.component-nav h3 {
  margin: 0 0 1rem 0;
  color: #495057;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.nav-list li a {
  color: #667eea;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.nav-list li a:hover {
  background-color: #e9ecef;
}

.storybook-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.component-section {
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 2rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.component-story {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 2rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.story-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  padding: 1.5rem 1.5rem 0.5rem 1.5rem;
}

.story-description {
  color: #6c757d;
  margin: 0;
  padding: 0 1.5rem 1rem 1.5rem;
  font-style: italic;
}

.story-showcase {
  padding: 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

/* Demo Form Styles */
.demo-form {
  max-width: 400px;
}

.demo-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  margin: 0.5rem 0.5rem 0.5rem 0;
  transition: background-color 0.2s;
}

.demo-button:hover {
  background: #5a6fd8;
}

.demo-button.success {
  background: #28a745;
}

.demo-button.error {
  background: #dc3545;
}

.demo-button.warning {
  background: #ffc107;
  color: #333;
}

.demo-button.btn-primary {
  background: #007bff;
}

/* Component Groups */
.chip-group {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.alert-group > * {
  margin-bottom: 1rem;
}

.link-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.checkbox-status {
  font-weight: 500;
  color: #495057;
}

.toast-controls {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Utility classes */
.item,
.box {
  background: #e9ecef;
  padding: 1rem;
  text-align: center;
  border-radius: 4px;
  margin: 0.25rem 0;
}

.bordered {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .storybook-header {
    padding: 2rem 1rem;
  }

  .library-title {
    font-size: 2rem;
  }

  .storybook-main {
    padding: 0 1rem;
  }

  .nav-list {
    flex-direction: column;
  }
}
</style>
